import * as React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
export default function LeadForm() {
  return <section data-section-id="258388" id="contactenquiry" className="bg-background text-foreground py-16 md:py-24">
      <div className="page-container">
        <div className="mb-12 text-center md:mb-16">
          <h2 className="text-primary mb-3 text-sm font-default font-bold tracking-widest uppercase">Request a Quote</h2>
          <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
            Tell us about your event and our technical team will provide a detailed production proposal.
          </p>
        </div>

        <div className="mx-auto max-w-3xl">
          <FormProvider formId="contact_inquiry_form" sectionName="lead-form" defaultValues={{
          full_name: "",
          email_address: "",
          phone_number: "",
          venue_name: "",
          venue_location: "",
          approximate_guest_numbers: "",
          event_type: "",
          event_date: "",
          additional_requirements: ""
        }} validationRules={{
          full_name: {
            required: true
          },
          email_address: {
            required: true,
            pattern: "email"
          },
          phone_number: {
            required: true
          },
          event_type: {
            required: true
          },
          event_date: {
            required: true
          }
        }} successMessage="Thank you for your enquiry. A member of the Pulse AV team will contact you shortly with a bespoke quote.">
            <InquiryForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="bg-card/50 border-primary/20 rounded-lg border p-8 text-center backdrop-blur-sm">
        <h3 className="text-primary mb-4 text-2xl font-bold">Enquiry Sent</h3>
        <p className="text-foreground mb-8">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary/10">
          Send Another Enquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="bg-card/30 border-border rounded-lg border p-6 md:p-10 backdrop-blur-md">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField control={control} name="full_name" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground font-bold">Full Name</FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" className="bg-background/50 border-border focus:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email_address" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground font-bold">Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john@example.com" className="bg-background/50 border-border focus:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone_number" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground font-bold">Phone Number</FormLabel>
                <FormControl>
                  <Input placeholder="07123 456789" className="bg-background/50 border-border focus:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="event_type" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground font-bold">Event Type</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-background/50 border-border focus:border-primary">
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="Wedding">Wedding</SelectItem>
                    <SelectItem value="Live Event">Live Event</SelectItem>
                    <SelectItem value="Conference and Gala Dinner">Conference and Gala Dinner</SelectItem>
                    <SelectItem value="Corporate Event">Corporate Event</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="venue_name" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground font-bold">Venue Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. The Savoy" className="bg-background/50 border-border focus:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="venue_location" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground font-bold">Venue Location</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. London, UK" className="bg-background/50 border-border focus:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="event_date" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground font-bold">Event Date</FormLabel>
                <FormControl>
                  <Input type="date" className="bg-background/50 border-border focus:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="approximate_guest_numbers" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground font-bold">Approx. Guest Numbers</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="150" className="bg-background/50 border-border focus:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="mt-6">
          <FormField control={control} name="additional_requirements" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground font-bold">Additional Requirements</FormLabel>
                <FormControl>
                  <Textarea placeholder="Tell us more about your vision, specific AV needs, or special effects..." className="min-h-[120px] bg-background/50 border-border focus:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        {submitError && <div className="mt-6 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
            <p>{submitError}</p>
          </div>}

        <div className="mt-8">
          <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-lg font-bold uppercase tracking-wider rounded-lg">
            {isSubmitting ? <>
                <Loader className="mr-2 h-5 w-5 animate-spin" />
                Processing...
              </> : "Send Enquiry"}
          </Button>
          <p className="text-muted-foreground mt-4 text-center text-xs">
            By submitting this form, you agree to our privacy policy. We will only use your data to provide the requested quote.
          </p>
        </div>
      </div>
    </Form>;
}